// pfs-page-header.ts — Seiten-Kopf (PFS-729, Epic PFS-591).
//
// Loest das ueber Route-Seiten (sparkden-identity, sparkden-main-Hero, …) hand-
// gerollte .page-head-Muster (<h1>+<p> mit eigenem CSS) ab. Anders als
// <pfs-card-header> (Header-Block INNERHALB einer Card) ist dies der Kopf einer
// ganzen Seite/Route — groesserer Titel, kein Icon-Kachel-Media-Object, keine
// Card-Huelle. Der Consumer platziert es ueber seinem Content.
//
// Attribute (alle data-*):
//   data-title    Seitentitel (h1)
//   data-subtitle optionale gedaempfte Beschreibungszeile darunter
// Slot (optional):
//   actions       frei gestaltbare rechte Zone (ans Ende geschoben) — z.B. ein
//                 Button/Toggle auf Seitenebene. Leer -> unsichtbar.
//
// Abstand zum Content besitzt der Consumer (Stack-/wrap-gap), nicht der Header
// [[feedback_gap_only_spacing]]. Voll brandbar (nur --pfs-color-*-Tokens).
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

const STYLES = `
:host { display: block; }
.head { display: flex; align-items: flex-start; gap: 1rem; }
.txt { display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; }
.title { margin: 0; font-size: 1.5rem; font-weight: 600; color: var(--pfs-color-on-surface); }
.title:empty { display: none; }
.subtitle { margin: 0; font-size: 0.875rem; color: var(--pfs-color-on-surface-muted); max-width: 70ch; }
.subtitle:empty { display: none; }
.actions { margin-left: auto; display: flex; align-items: center; gap: 0.5rem; }
`;

// Slotted-Wrapper nur zeigen, wenn befuellt.
function hideWhenEmpty(wrap: HTMLElement, slot: HTMLSlotElement): void {
  const sync = (): void => {
    wrap.style.display = slot.assignedNodes({ flatten: true }).length ? "" : "none";
  };
  slot.addEventListener("slotchange", sync);
  sync();
}

defineComponent({
  tagName: "pfs-page-header",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const head = document.createElement("div");
    head.className = "head";

    const txt = document.createElement("span");
    txt.className = "txt";
    txt.innerHTML = `<h1 class="title"></h1><p class="subtitle"></p>`;
    const titleEl = txt.querySelector(".title") as HTMLElement;
    const subtitleEl = txt.querySelector(".subtitle") as HTMLElement;

    const actions = document.createElement("div");
    actions.className = "actions";
    actions.innerHTML = `<slot name="actions"></slot>`;

    head.append(txt, actions);
    root.append(head);

    hideWhenEmpty(actions, actions.querySelector("slot") as HTMLSlotElement);

    const paint = (): void => {
      titleEl.textContent = host.dataset.title ?? "";
      subtitleEl.textContent = host.dataset.subtitle ?? "";
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-title", "data-subtitle"],
    });
  },
});
